/**
 * Counter — animiert Zahlen von 0 auf Zielwert beim Einscrollen
 *
 * Markup:
 *   <span data-counter data-target="1.200">1.200</span>
 *   <span data-counter data-target="100 %">100 %</span>
 *   <span data-counter data-target="seit 2024">seit 2024</span>  (kein number → no animation)
 *
 * Verhalten:
 *  - Erste Zahl im String wird animiert; Prefix/Suffix bleibt erhalten
 *  - Thousands-Separator (de-DE: "1.200") wird beim Animieren beibehalten
 *  - prefers-reduced-motion: sofort den Zielwert setzen
 *  - Nur einmal pro Element (unobserve nach ersten Animation)
 */

const DURATION = 1400;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function parseNumberLocale(numStr) {
  // de-DE: "1.200,5" → 1200.5
  if (/^\d+\.\d{3}/.test(numStr)) {
    return { value: parseFloat(numStr.replace(/\./g, "").replace(",", ".")), thousandsSep: true };
  }
  return { value: parseFloat(numStr.replace(",", ".")), thousandsSep: false };
}

function formatNumber(num, thousandsSep) {
  if (thousandsSep) return Math.round(num).toLocaleString("de-DE");
  return Math.round(num).toString();
}

function animate(el) {
  const target = el.dataset.target ?? el.textContent.trim();
  const match = target.match(/^(.*?)([\d.,]+)(.*)$/);
  if (!match) return;
  const [, prefix, numStr, suffix] = match;
  const { value: targetNum, thousandsSep } = parseNumberLocale(numStr);
  if (isNaN(targetNum)) return;

  const start = performance.now();
  function tick(now) {
    const t = Math.min(1, (now - start) / DURATION);
    const eased = easeOutCubic(t);
    el.textContent = prefix + formatNumber(targetNum * eased, thousandsSep) + suffix;
    if (t < 1) requestAnimationFrame(tick);
    else el.textContent = target; // exact target
  }
  el.textContent = prefix + formatNumber(0, thousandsSep) + suffix;
  requestAnimationFrame(tick);
}

export function init() {
  const targets = document.querySelectorAll("[data-counter]");
  if (targets.length === 0) return;

  if (
    typeof IntersectionObserver === "undefined" ||
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  ) {
    // Reduced motion or no IO: just leave the original text in place
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animate(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );

  targets.forEach((el) => observer.observe(el));
}
